import { Tooltip as ChakraTooltip, Portal } from '@chakra-ui/react';
import * as React from 'react';

export interface TooltipProps extends ChakraTooltip.RootProps {
	showArrow?: boolean;
	portalled?: boolean;
	portalRef?: React.RefObject<HTMLElement>;
	content: React.ReactNode;
	contentProps?: ChakraTooltip.ContentProps;
	disabled?: boolean;
}

const TOOLTIP_POSITION_VAR = {
	X: '--x',
	Y: '--y',
} as const;

const useTooltipPositionVisibility = () => {
	const [isPositioned, setIsPositioned] = React.useState(false);
	const [positionerEl, setPositionerEl] = React.useState<HTMLDivElement | null>(null);

	React.useLayoutEffect(() => {
		if (!positionerEl || isPositioned) return;

		const updatePositioned = () => {
			const styles = window.getComputedStyle(positionerEl);
			setIsPositioned(
				Boolean(
					styles.getPropertyValue(TOOLTIP_POSITION_VAR.X) && styles.getPropertyValue(TOOLTIP_POSITION_VAR.Y),
				),
			);
		};

		updatePositioned();

		const observer = new MutationObserver(updatePositioned);
		observer.observe(positionerEl, { attributes: true, attributeFilter: ['style'] });

		return () => observer.disconnect();
	}, [isPositioned, positionerEl]);

	return {
		isPositioned,
		resetPositioned: () => setIsPositioned(false),
		setPositionerEl,
	};
};

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(props, ref) {
	const { showArrow, children, disabled, portalled = true, content, contentProps, portalRef, ...rest } = props;
	const { isPositioned, resetPositioned, setPositionerEl } = useTooltipPositionVisibility();

	const handleOpenChange: TooltipProps['onOpenChange'] = (details) => {
		if (!details.open) resetPositioned();
		rest.onOpenChange?.(details);
	};

	// Passed down rather than short-circuited: returning `children` alone changes the tree shape, so
	// a consumer toggling `disabled` — a trigger that hides its tooltip while its own popover is
	// open, say — unmounts and remounts the trigger every time instead of just withholding the label.
	return (
		<ChakraTooltip.Root {...rest} disabled={disabled} onOpenChange={handleOpenChange}>
			<ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
			<Portal disabled={!portalled} container={portalRef}>
				<ChakraTooltip.Positioner ref={setPositionerEl} visibility={isPositioned ? 'visible' : 'hidden'}>
					<ChakraTooltip.Content
						ref={ref}
						px={3}
						py={2}
						bg="surface.default"
						color="brand.fg"
						borderWidth="1px"
						borderColor="border.default"
						boxShadow="md"
						css={{ '--arrow-background': 'var(--chakra-colors-surface-default)' }}
						borderRadius="xl"
						textStyle="body.sm"
						fontWeight="normal"
						lineHeight="normal"
						maxW={{ base: 'calc(100vw - 2rem)', md: '360px' }}
						{...contentProps}
					>
						{showArrow && (
							<ChakraTooltip.Arrow>
								{/* The tip is a square that zag rotates so its top-left corner points at the
								    trigger — 45° below it, 225° above, and so on — so the top and left
								    borders are the two exposed edges whichever side the tooltip lands on.
								    Without them the outline stops dead where the arrow joins the bubble. */}
								<ChakraTooltip.ArrowTip
									borderTopWidth="1px"
									borderLeftWidth="1px"
									borderColor="border.default"
								/>
							</ChakraTooltip.Arrow>
						)}
						{content}
					</ChakraTooltip.Content>
				</ChakraTooltip.Positioner>
			</Portal>
		</ChakraTooltip.Root>
	);
});
